import { useEffect, useState, useCallback, useMemo } from "react";
import { Badge, Button, Card, Col, Form, Modal, Row, Spinner, Table } from "react-bootstrap";
import { FaPlus, FaPen, FaTrash, FaChevronDown, FaChevronRight, FaSync } from "react-icons/fa";
import Select from "react-select";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import axiosInstance from "../../axiosInstance";
import AdLayout from "../../components/AdLayout";
import { fetchAccountingPermissions, ACCOUNTING_CONFIG_EXPENSE_OBJECT } from "../../libs/customerCreatePermission";
import "./accountingResponsive.css";

const unwrap = (res) => res?.data?.data ?? res?.data ?? {};
const isOk = (res) => res?.data?.success !== false;
const cardStyle = { boxShadow: "0 2px 8px rgba(0,0,0,0.1)" };

const INITIAL_FORM = {
  code: "",
  name: "",
  categoryId: "",
  lineIds: [],
};

const flattenTree = (nodes, depth = 0, out = []) => {
  (nodes || []).forEach((n) => {
    out.push({ ...n, depth });
    flattenTree(n.children || [], depth + 1, out);
  });
  return out;
};

const sourceBadge = (row) => {
  if (row.sourceType === "PARTNER") return { bg: "secondary", label: "Đối tác" };
  if (row.sourceType === "EMPLOYEE") return { bg: "primary", label: "Nhân viên" };
  if (row.isSystem) return { bg: "dark", label: "Hệ thống" };
  return { bg: "info", label: "Thủ công" };
};

const ExpenseObjectList = ({ embedded = false }) => {
  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [lineOptions, setLineOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(INITIAL_FORM);
  const [keyword, setKeyword] = useState("");
  const [statusFilter, setStatusFilter] = useState("ACTIVE");
  const [openGroups, setOpenGroups] = useState({});

  const [syncing, setSyncing] = useState(false);
  const [perm, setPerm] = useState({ canCreate: false, canEdit: false, canDelete: false });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const p = await fetchAccountingPermissions(axiosInstance, ACCOUNTING_CONFIG_EXPENSE_OBJECT);
      if (!cancelled) {
        setPerm({
          canCreate: Boolean(p?.canCreate),
          canEdit: Boolean(p?.canEdit),
          canDelete: Boolean(p?.canDelete),
        });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await axiosInstance.get("/api/accounting/expense-categories/management-tree");
      setCategories(flattenTree(Array.isArray(unwrap(res)) ? unwrap(res) : []));
    } catch {
      /* ignore */
    }
  }, []);

  const fetchLines = useCallback(async () => {
    try {
      const res = await axiosInstance.get("/api/accounting/payment-vouchers/lines");
      const data = unwrap(res);
      const list = Array.isArray(data) ? data : [];
      setLineOptions(list.map((l) => ({ value: String(l.id), label: l.name || `Line ${l.id}` })));
    } catch {
      /* ignore */
    }
  }, []);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: 0, size: 500, status: statusFilter || "ACTIVE" };
      if (keyword.trim()) params.keyword = keyword.trim();
      const res = await axiosInstance.get("/api/accounting/expense-objects", { params });
      const body = unwrap(res);
      const content = body?.content ?? (Array.isArray(body) ? body : []);
      setRows(content);
    } catch {
      toast.error("Không thể tải đối tượng chi");
    } finally {
      setLoading(false);
    }
  }, [keyword, statusFilter]);

  useEffect(() => {
    fetchCategories();
    fetchLines();
  }, [fetchCategories, fetchLines]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const groups = useMemo(() => {
    const map = new Map();
    rows.forEach((row) => {
      const key = row.categoryId != null ? String(row.categoryId) : "none";
      const title = row.categoryName || "Không có danh mục";
      if (!map.has(key)) map.set(key, { key, title, items: [] });
      map.get(key).items.push(row);
    });
    return Array.from(map.values()).sort((a, b) => a.title.localeCompare(b.title, "vi"));
  }, [rows]);

  useEffect(() => {
    setOpenGroups((prev) => {
      const next = { ...prev };
      let changed = false;
      groups.forEach((g) => {
        if (next[g.key] === undefined) {
          next[g.key] = true;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [groups]);

  const toggleGroup = (key) => {
    setOpenGroups((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const onFormChange = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const openCreate = () => {
    setEditId(null);
    setForm(INITIAL_FORM);
    setShowModal(true);
  };

  const openEdit = (row) => {
    if (row.isSystem) return;
    setEditId(row.id);
    setForm({
      code: row.code || "",
      name: row.name || "",
      categoryId: row.categoryId ? String(row.categoryId) : "",
      lineIds: (row.lineIds || []).map(String),
    });
    setShowModal(true);
  };

  const handleSyncSystem = async () => {
    const result = await Swal.fire({
      title: "Đồng bộ mặc định hệ thống?",
      text: "Khôi phục danh mục chi (Đối soát ĐT, Chi lương, Chi phí trả trước) và đối tượng chi hệ thống (Nhân viên, đối tác, nhân viên nội bộ).",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Đồng bộ",
      cancelButtonText: "Hủy",
    });
    if (!result.isConfirmed) return;
    setSyncing(true);
    try {
      const res = await axiosInstance.post("/api/accounting/expense-objects/sync-system");
      if (!isOk(res)) {
        toast.error(res?.data?.message || "Đồng bộ thất bại");
        return;
      }
      toast.success(res?.data?.message || "Đã đồng bộ mặc định hệ thống");
      await Promise.all([fetchCategories(), fetchRows()]);
    } catch (err) {
      toast.error(err.response?.data?.message || "Đồng bộ thất bại");
    } finally {
      setSyncing(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.code || !form.name || !form.categoryId) {
      toast.warn("Vui lòng nhập mã, tên và danh mục chi");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        code: form.code.trim(),
        name: form.name.trim(),
        categoryId: Number(form.categoryId),
        lineIds: (form.lineIds || []).map((id) => Number(id)).filter((id) => !Number.isNaN(id)),
      };
      const res = editId
        ? await axiosInstance.put(`/api/accounting/expense-objects/${editId}`, payload)
        : await axiosInstance.post("/api/accounting/expense-objects", payload);
      if (!isOk(res)) {
        toast.error(res?.data?.message || "Lưu đối tượng chi thất bại");
        return;
      }
      toast.success(editId ? "Cập nhật thành công" : "Thêm đối tượng chi thành công");
      setShowModal(false);
      fetchRows();
    } catch (err) {
      toast.error(err.response?.data?.message || "Lưu đối tượng chi thất bại");
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggle = async (row) => {
    if (row.isSystem && row.active) {
      toast.warn("Không thể tắt đối tượng chi mặc định của hệ thống");
      return;
    }
    try {
      const res = await axiosInstance.patch(`/api/accounting/expense-objects/${row.id}/active`, {
        active: !row.active,
      });
      if (!isOk(res)) {
        toast.error(res?.data?.message || "Cập nhật trạng thái thất bại");
        return;
      }
      toast.success(row.active ? "Đã tắt đối tượng chi" : "Đã bật đối tượng chi");
      fetchRows();
    } catch (err) {
      toast.error(err.response?.data?.message || "Cập nhật trạng thái thất bại");
    }
  };

  const handleDelete = async (row) => {
    if (row.isSystem) return;
    const result = await Swal.fire({
      title: "Xóa đối tượng chi?",
      text: `Bạn có chắc muốn xóa "${row.name}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Xóa",
      cancelButtonText: "Hủy",
      confirmButtonColor: "#dc3545",
    });
    if (!result.isConfirmed) return;
    try {
      const res = await axiosInstance.delete(`/api/accounting/expense-objects/${row.id}`);
      if (!isOk(res)) {
        toast.error(res?.data?.message || "Xóa thất bại");
        return;
      }
      toast.success("Đã xóa đối tượng chi");
      fetchRows();
    } catch (err) {
      toast.error(err.response?.data?.message || "Xóa thất bại");
    }
  };

  const selectedLineOptions = useMemo(
    () => lineOptions.filter((o) => (form.lineIds || []).includes(o.value)),
    [lineOptions, form.lineIds]
  );

  const renderRow = (row) => {
    const badge = sourceBadge(row);
    return (
      <tr key={row.id} className={!row.active ? "table-secondary" : undefined}>
        <td className="acc-eo-col-code" title={row.code}>{row.code}</td>
        <td className="acc-eo-col-name" title={row.name}>{row.name}</td>
        <td className="acc-eo-col-lines">
          <div className="d-flex flex-wrap gap-1 align-items-center">
            {(row.lineNames || []).length ? (
              row.lineNames.map((name, i) => (
                <span key={`${row.id}-line-${i}`} className="select3-item acc-line-tag">
                  {name}
                </span>
              ))
            ) : (
              <span className="text-muted">—</span>
            )}
          </div>
        </td>
        <td className="acc-eo-col-source">
          <Badge bg={badge.bg}>{badge.label}</Badge>
        </td>
        <td className="acc-eo-col-status">
          <Form.Check
            type="switch"
            id={`exp-obj-active-${row.id}`}
            checked={!!row.active}
            disabled={!!row.isSystem || !perm.canEdit}
            onChange={() => handleToggle(row)}
            label={row.active ? "Bật" : "Tắt"}
          />
        </td>
        <td className="acc-eo-col-actions text-center">
          <div className="d-flex gap-1 justify-content-center">
            {!row.isSystem && (
              <>
                {perm.canEdit && (
                  <Button variant="outline-primary" size="sm" onClick={() => openEdit(row)}>
                    <FaPen />
                  </Button>
                )}
                {perm.canDelete && (
                  <Button variant="outline-danger" size="sm" onClick={() => handleDelete(row)}>
                    <FaTrash />
                  </Button>
                )}
              </>
            )}
          </div>
        </td>
      </tr>
    );
  };

  const body = (
    <div className={embedded ? "" : "acc-page"}>
      <Card style={cardStyle}>
        <Card.Body>
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
            <h5 className="mb-0 fw-bold">Đối tượng chi</h5>
            <div className="d-flex gap-2">
              {perm.canEdit && (
                <Button variant="outline-secondary" size="sm" onClick={handleSyncSystem} disabled={syncing}>
                  {syncing ? <Spinner animation="border" size="sm" className="me-1" /> : <FaSync className="me-1" />}
                  Đồng bộ mặc định hệ thống
                </Button>
              )}
              {perm.canCreate && (
                <Button variant="primary" size="sm" onClick={openCreate}>
                  <FaPlus className="me-1" /> Thêm đối tượng
                </Button>
              )}
            </div>
          </div>

          <Row className="g-2 mb-3 align-items-center">
            <Col xs={12} md={6}>
              <Form.Control
                size="sm"
                placeholder="Tìm mã / tên..."
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
              />
            </Col>
            <Col xs={12} md={6}>
              <div className="acc-status-radio" role="group" aria-label="Lọc trạng thái">
                <input
                  type="radio"
                  className="btn-check"
                  name="exp-obj-status"
                  id="exp-obj-status-enable"
                  checked={statusFilter === "ACTIVE"}
                  onChange={() => setStatusFilter("ACTIVE")}
                />
                <label
                  className="btn btn-sm acc-status-radio-btn acc-status-radio-enable"
                  htmlFor="exp-obj-status-enable"
                >
                  Enable
                </label>
                <input
                  type="radio"
                  className="btn-check"
                  name="exp-obj-status"
                  id="exp-obj-status-disable"
                  checked={statusFilter === "INACTIVE"}
                  onChange={() => setStatusFilter("INACTIVE")}
                />
                <label
                  className="btn btn-sm acc-status-radio-btn acc-status-radio-disable"
                  htmlFor="exp-obj-status-disable"
                >
                  Disable
                </label>
              </div>
            </Col>
          </Row>

          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : groups.length === 0 ? (
            <div className="text-center text-muted py-4">Chưa có đối tượng chi</div>
          ) : (
            <div className="acc-expense-object-groups">
              {groups.map((group) => {
                const open = !!openGroups[group.key];
                return (
                  <div key={group.key} className="acc-expense-object-group mb-2">
                    <button
                      type="button"
                      className={`acc-expense-object-group-header${open ? " is-open" : ""}`}
                      onClick={() => toggleGroup(group.key)}
                      aria-expanded={open}
                    >
                      <span className="d-flex align-items-center gap-2">
                        <span className="acc-expense-object-group-chevron">
                          {open ? <FaChevronDown /> : <FaChevronRight />}
                        </span>
                        <span className="acc-expense-object-group-title">{group.title}</span>
                        <span className="acc-expense-object-group-count">{group.items.length}</span>
                      </span>
                    </button>
                    {open && (
                      <div className="acc-table-wrap">
                        <Table striped hover className="mb-0 align-middle acc-expense-object-table">
                          <colgroup>
                            <col className="acc-eo-col-code" />
                            <col className="acc-eo-col-name" />
                            <col className="acc-eo-col-lines" />
                            <col className="acc-eo-col-source" />
                            <col className="acc-eo-col-status" />
                            <col className="acc-eo-col-actions" />
                          </colgroup>
                          <thead className="table-light">
                            <tr>
                              <th>Mã</th>
                              <th>Tên</th>
                              <th>Line kinh doanh</th>
                              <th>Nguồn</th>
                              <th>Trạng thái</th>
                              <th className="text-center">Thao tác</th>
                            </tr>
                          </thead>
                          <tbody>{group.items.map(renderRow)}</tbody>
                        </Table>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{editId ? "Sửa đối tượng chi" : "Thêm đối tượng chi"}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSave}>
          <Modal.Body>
            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Mã</Form.Label>
                  <Form.Control
                    value={form.code}
                    onChange={(e) => onFormChange("code", e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Tên</Form.Label>
                  <Form.Control
                    value={form.name}
                    onChange={(e) => onFormChange("name", e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Danh mục chi</Form.Label>
                  <Form.Select
                    value={form.categoryId}
                    onChange={(e) => onFormChange("categoryId", e.target.value)}
                  >
                    <option value="">-- Chọn danh mục --</option>
                    {categories.map((c) => (
                      <option key={c.id} value={c.id} disabled={!c.active && String(c.id) !== form.categoryId}>
                        {`${" ".repeat((c.depth || 0) * 2)}${c.name}${!c.active ? " (tắt)" : ""}`}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Line kinh doanh</Form.Label>
                  <Select
                    isMulti
                    options={lineOptions}
                    value={selectedLineOptions}
                    onChange={(opts) => onFormChange("lineIds", (opts || []).map((o) => o.value))}
                    placeholder="Chọn line..."
                    classNamePrefix="react-select"
                    styles={{
                      multiValue: (base) => ({
                        ...base,
                        backgroundColor: "#a9e7af",
                        borderRadius: 4,
                      }),
                      multiValueLabel: (base) => ({
                        ...base,
                        color: "#000",
                        padding: "2px 4px",
                      }),
                      multiValueRemove: (base) => ({
                        ...base,
                        color: "#000",
                        ":hover": { backgroundColor: "rgba(0,0,0,0.08)", color: "#000" },
                      }),
                    }}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Đóng
            </Button>
            <Button variant="primary" type="submit" disabled={submitting}>
              {submitting ? <Spinner animation="border" size="sm" /> : "Lưu"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );

  if (embedded) return body;
  return <AdLayout>{body}</AdLayout>;
};

export default ExpenseObjectList;
